import React from "react";
import { useNotifications } from "../../context/NotificationContext";
import { useTheme } from "../../ThemeContext";
import NotificationItem from "./NotificationItem";
import { IoCheckmarkDoneOutline, IoClose } from "react-icons/io5";

const ORANGE = "#ffbe63";

export default function NotificationDropdown({ isOpen, onClose }) {
  const {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
  } = useNotifications();
  const { theme } = useTheme();

  if (!isOpen) return null;

  const isDark = theme === "dark";

  return (
    <div
      className={`fixed sm:absolute right-2 sm:right-0 top-16 sm:top-full mt-2 w-[calc(100vw-1rem)] sm:w-96 max-h-[80vh] sm:max-h-[500px] rounded-2xl shadow-2xl border flex flex-col z-[300] overflow-hidden ${
        isDark
          ? "bg-[#1e1e26] border-gray-800 text-white"
          : "bg-white border-gray-200 text-gray-900"
      }`}
      style={{
        boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        className={`px-4 py-3 border-b flex items-center justify-between ${
          isDark ? "border-gray-800 bg-[#252530]" : "border-gray-100 bg-[#faf8f5]"
        }`}
      >
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-sm sm:text-base">Notifications</h3>
          {unreadCount > 0 && (
            <span
              className="text-[11px] font-bold px-2 py-0.5 rounded-full text-black"
              style={{ background: ORANGE }}
            >
              {unreadCount > 99 ? "99+" : unreadCount} new
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs font-semibold flex items-center gap-1 hover:opacity-80 transition-opacity"
              style={{ color: ORANGE }}
              title="Mark all notifications as read"
            >
              <IoCheckmarkDoneOutline className="text-sm" />
              <span>Mark all read</span>
            </button>
          )}

          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-200 transition-colors"
            aria-label="Close notifications"
          >
            <IoClose className="text-lg" />
          </button>
        </div>
      </div>

      {/* Notification List Body */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-800/40">
        {loading && notifications.length === 0 ? (
          <div className="py-12 flex flex-col items-center justify-center gap-2 text-gray-400">
            <div
              className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${ORANGE} transparent transparent transparent` }}
            />
            <span className="text-xs">Loading notifications...</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-12 px-4 flex flex-col items-center justify-center gap-2 text-gray-400 text-center">
            <span className="text-3xl">🔔</span>
            <p className="text-sm font-medium">No notifications yet</p>
            <p className="text-xs text-gray-500">
              When tasks and checklists are assigned, they will appear here.
            </p>
          </div>
        ) : (
          notifications.map((notif) => (
            <NotificationItem
              key={notif.id}
              notification={notif}
              onMarkRead={markRead}
              onItemClick={onClose}
            />
          ))
        )}
      </div>
    </div>
  );
}
